// @flow

import React, { PureComponent } from 'react';
import i18n from 'app/common/lib/i18n';
import { Box } from 'gestalt';
import { DefaultFontFamily } from 'app/common/react/components/common/FontFamily';

const styles = {
  input: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#333',
    fontFamily: DefaultFontFamily,
    fontSize: 16,
    fontWeight: 600,
    height: '100%',
    outline: 'none',
    padding: '0',
    width: '100%',
  },
};

type Props = {|
  isFocused: boolean,
  onClick?: (event?: SyntheticKeyboardEvent<HTMLInputElement>) => void,
  onInputBlur: () => void,
  onInputFocus: (event?: SyntheticKeyboardEvent<HTMLInputElement>) => void,
  onKeyDown: (event: SyntheticKeyboardEvent<HTMLInputElement>) => void,
  onSearchTrigger: () => void,
  onTextChange: (query: string) => void,
  placeholder?: string,
  textValue: string,
|};

export default class SearchInput extends PureComponent<Props> {
  componentDidMount() {
    const { isFocused, onSearchTrigger } = this.props;

    // Focus is a managed prop, so make sure we're honoring it.
    this.setFocus(isFocused);

    // This custom event is emitted by the search PWT test for performance
    // regression testing purposes.
    if (this._input.current) {
      this._input.current.addEventListener('searchTrigger', onSearchTrigger);
    }
  }

  componentDidUpdate() {
    const { isFocused } = this.props;

    // Focus is a managed prop, so make sure we're honoring it.
    this.setFocus(isFocused);
  }

  setFocus(focus: boolean) {
    if (!this._input.current) {
      return;
    }

    const inputIsFocused = document.activeElement === this._input.current;

    if (inputIsFocused !== focus) {
      if (focus) {
        this._input.current.focus();
      } else {
        this._input.current.blur();
      }
    }
  }

  _input: { current: null | HTMLInputElement } = React.createRef();

  handleOnChange = (event: SyntheticInputEvent<HTMLInputElement>) => {
    const { onTextChange } = this.props;
    onTextChange(event.target.value);
  };

  render() {
    const {
      onInputBlur,
      onInputFocus,
      onClick = () => {},
      onKeyDown,
      placeholder,
      textValue,
    } = this.props;

    return (
      <Box height="100%" flex="grow">
        <React.Fragment>
          <input
            autoCapitalize="off"
            autoCorrect="off"
            autoComplete="off"
            aria-autocomplete="list"
            aria-describedby="SearchInputAccessibleText"
            aria-controls="SuggestionsMenu"
            className="SearchBoxInputExperimental"
            data-test-id="search-box-input"
            name="searchBoxInput"
            onBlur={onInputBlur}
            onChange={this.handleOnChange}
            onClick={onClick}
            onFocus={onInputFocus}
            onKeyDown={onKeyDown}
            placeholder={placeholder || i18n._('Search')}
            ref={this._input}
            style={styles.input}
            type="text"
            value={textValue}
          />
        </React.Fragment>
      </Box>
    );
  }
}
